import React, { useState, useRef } from 'react'
import api from '../services/api'
import { toast } from 'sonner'
import {
  X,
  LinkedinLogo,
  Image,
  FilePdf,
  Link,
  Eye,
  Users,
  PaperPlaneTilt,
  Trash,
} from '@phosphor-icons/react'

const VISIBILITY_OPTIONS = [
  { value: 'PUBLIC', label: 'Público', icon: Eye, description: 'Visible para todos en LinkedIn' },
  {
    value: 'CONNECTIONS',
    label: 'Solo contactos',
    icon: Users,
    description: 'Solo tus contactos de 1er grado',
  },
]

const MAX_TEXT_LENGTH = 3000

export default function LinkedInPublishModal({ onClose, onPublished, initialText = '' }) {
  const [text, setText] = useState(initialText)
  const [visibility, setVisibility] = useState('PUBLIC')
  const [articleUrl, setArticleUrl] = useState('')
  const [showArticleField, setShowArticleField] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [fileType, setFileType] = useState(null) // 'image' | 'document'
  const [publishing, setPublishing] = useState(false)
  const imageInputRef = useRef(null)
  const docInputRef = useRef(null)

  const charsLeft = MAX_TEXT_LENGTH - text.length
  const canPublish = text.trim().length > 0 && !publishing

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0]
    if (!file) return
    setSelectedFile(file)
    setFileType(type)
    setShowArticleField(false)
    setArticleUrl('')
  }

  const removeFile = () => {
    setSelectedFile(null)
    setFileType(null)
    if (imageInputRef.current) imageInputRef.current.value = ''
    if (docInputRef.current) docInputRef.current.value = ''
  }

  const handlePublish = async () => {
    if (!canPublish) return
    setPublishing(true)

    try {
      let postUrn = null

      if (selectedFile && fileType === 'image') {
        const formData = new FormData()
        formData.append('text', text)
        formData.append('visibility', visibility)
        formData.append('image', selectedFile)
        const { data } = await api.post('/linkedin/posts/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        postUrn = data.post_urn
      } else if (selectedFile && fileType === 'document') {
        const formData = new FormData()
        formData.append('text', text)
        formData.append('visibility', visibility)
        formData.append('title', selectedFile.name.replace(/\.[^/.]+$/, ''))
        formData.append('document', selectedFile)
        const { data } = await api.post('/linkedin/posts/document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        postUrn = data.post_urn
      } else {
        const payload = {
          text,
          visibility,
          article_url: showArticleField && articleUrl ? articleUrl : null,
        }
        const { data } = await api.post('/linkedin/posts', payload)
        postUrn = data.post_urn
      }

      onPublished?.(postUrn)
      onClose()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al publicar en LinkedIn')
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
      <div
        className="bg-ds-surface border border-ds-border w-full max-w-lg flex flex-col"
        style={{ borderRadius: 2 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ds-border">
          <div className="flex items-center gap-2">
            <LinkedinLogo size={20} color="#0077B5" weight="fill" />
            <span className="text-white font-medium text-sm">Publicar en LinkedIn</span>
          </div>
          <button onClick={onClose} className="text-[#6E6E73] hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 flex-1 overflow-y-auto">
          {/* Text area */}
          <div>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              maxLength={MAX_TEXT_LENGTH}
              placeholder="¿Qué quieres compartir?"
              rows={6}
              className="w-full bg-ds-elevated border border-ds-border text-white text-sm placeholder-[#6E6E73] px-3 py-2.5 resize-none focus:outline-none focus:border-[#0077B5]/50 transition-colors"
              style={{ borderRadius: 2 }}
            />
            <p
              className={`text-right text-[11px] mt-1 ${charsLeft < 100 ? 'text-[#FF9F0A]' : 'text-[#6E6E73]'}`}
            >
              {charsLeft} caracteres restantes
            </p>
          </div>

          {/* Visibility selector */}
          <div>
            <p className="text-[#6E6E73] text-xs mb-2 font-medium uppercase tracking-wider">
              Visibilidad
            </p>
            <div className="flex gap-2">
              {VISIBILITY_OPTIONS.map(({ value, label, icon: Icon, description }) => (
                <button
                  key={value}
                  onClick={() => setVisibility(value)}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 border text-left transition-colors ${
                    visibility === value
                      ? 'border-[#0077B5]/50 bg-[rgba(0,119,181,0.08)] text-white'
                      : 'border-ds-border text-[#6E6E73] hover:border-[#0077B5]/30'
                  }`}
                  style={{ borderRadius: 2 }}
                >
                  <Icon size={14} className={visibility === value ? 'text-[#0077B5]' : ''} />
                  <div>
                    <p className="text-xs font-medium">{label}</p>
                    <p className="text-[10px] text-[#6E6E73]">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Attachment: selected file preview */}
          {selectedFile && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-ds-elevated border border-ds-border"
              style={{ borderRadius: 2 }}
            >
              {fileType === 'image' ? (
                <Image size={16} color="#0077B5" />
              ) : (
                <FilePdf size={16} color="#0077B5" />
              )}
              <span className="text-white text-xs flex-1 truncate">{selectedFile.name}</span>
              <button
                onClick={removeFile}
                className="text-[#6E6E73] hover:text-ds-red transition-colors"
              >
                <Trash size={14} />
              </button>
            </div>
          )}

          {/* Article URL field */}
          {showArticleField && !selectedFile && (
            <div>
              <input
                type="url"
                value={articleUrl}
                onChange={e => setArticleUrl(e.target.value)}
                placeholder="https://ejemplo.com/tu-articulo"
                className="w-full bg-ds-elevated border border-ds-border text-white text-sm placeholder-[#6E6E73] px-3 py-2 focus:outline-none focus:border-[#0077B5]/50 transition-colors"
                style={{ borderRadius: 2 }}
              />
            </div>
          )}
        </div>

        {/* Footer: attachment controls + publish */}
        <div className="px-5 py-4 border-t border-ds-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            {/* Image upload */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={e => handleFileSelect(e, 'image')}
            />
            <button
              onClick={() => imageInputRef.current?.click()}
              disabled={!!selectedFile || showArticleField}
              title="Adjuntar imagen"
              className="p-2 text-[#6E6E73] hover:text-[#0077B5] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Image size={18} />
            </button>

            {/* PDF/Document upload */}
            <input
              ref={docInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={e => handleFileSelect(e, 'document')}
            />
            <button
              onClick={() => docInputRef.current?.click()}
              disabled={!!selectedFile || showArticleField}
              title="Adjuntar documento / carousel (PDF)"
              className="p-2 text-[#6E6E73] hover:text-[#0077B5] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <FilePdf size={18} />
            </button>

            {/* Article URL */}
            <button
              onClick={() => {
                setShowArticleField(v => !v)
                removeFile()
              }}
              title="Añadir enlace a artículo"
              className={`p-2 transition-colors ${
                showArticleField ? 'text-[#0077B5]' : 'text-[#6E6E73] hover:text-[#0077B5]'
              }`}
            >
              <Link size={18} />
            </button>
          </div>

          <button
            onClick={handlePublish}
            disabled={!canPublish}
            className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] hover:bg-[#005885] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: 2 }}
          >
            {publishing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin" />
            ) : (
              <PaperPlaneTilt size={15} weight="fill" />
            )}
            {publishing ? 'Publicando…' : 'Publicar'}
          </button>
        </div>
      </div>
    </div>
  )
}
